import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Профиль авторизованного юзера — повторяет схему User из API-контракта
// (поля заполняются ответом /auth/sms/verify; в Phase 1A все три обязательны).
export interface AuthUser {
  id: string;
  phone: string | null;
  createdAt: string;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  // Нужно ли (пере)собрать согласие на обработку ПДн у текущего юзера. Источник истины —
  // сервер (ответы verify/telegram, /auth/me); фронт лишь хранит флаг и снимает после /auth/consent.
  consentRequired: boolean;
  // Дано ли согласие на спец-категории (Часть 2: здоровье/аллергии, диета). Источник истины —
  // сервер (/auth/me.specialConsent); фронт хранит флаг и гейтит спец-данные в «Семье».
  specialConsent: boolean;
  // Сохранить сессию целиком — ровно тот payload, что вернул /auth/sms/verify.
  setSession: (s: { accessToken: string; refreshToken: string; user: AuthUser; consentRequired?: boolean }) => void;
  // Точечно выставить флаг согласия (после /auth/consent или ревалидации /auth/me).
  setConsentRequired: (v: boolean) => void;
  // Точечно выставить флаг спец-согласия (после /auth/consent {special} или ревалидации /auth/me).
  setSpecialConsent: (v: boolean) => void;
  // Сброс сессии (кнопки «Выход» в Phase 1A ещё нет, но это базовый контракт стора).
  logout: () => void;
  isAuthenticated: () => boolean;
}

// persist в localStorage гидратируется СИНХРОННО при создании стора → Protected на первой
// отрисовке уже видит токен (нет «вспышки» логина). Инвариант держится только на синхронном
// сторадже: при переходе на async-сторадж (Capacitor/RuStore) понадобится gate _hasHydrated.
export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      consentRequired: false,
      specialConsent: false,
      setSession: ({ accessToken, refreshToken, user, consentRequired }) =>
        set({ accessToken, refreshToken, user, consentRequired: consentRequired ?? false }),
      setConsentRequired: (v) => set({ consentRequired: v }),
      setSpecialConsent: (v) => set({ specialConsent: v }),
      logout: () =>
        set({ accessToken: null, refreshToken: null, user: null, consentRequired: false, specialConsent: false }),
      isAuthenticated: () => !!get().accessToken,
    }),
    { name: 'recipt-auth' },
  ),
);
